import React from "react";
import { Form, FormikProvider, useFormik } from "formik";
import { z } from "zod";

import { useEditEntityMutation } from "@api/entity.api";
import { useEntity } from "@components/entity/useEntity";
import { createFormikFields } from "@components/forms/fields/createFormikFields";
import { PlainModal, StandardModalHeader } from "@design-system/Modal/Modal";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { VStack } from "@design-system/VStack/VStack";
import { submitHelper, validateFormikWithZodSchema } from "@helpers/form";
import { EditModalFooter } from "./EditModalFooter";

const updateWebsiteFormSchema = z.object({
    website: z.union([z.string().url("Invalid URL"), z.literal("")]),
});

type UpdateWebsiteFormValues = z.infer<typeof updateWebsiteFormSchema>;

const fields = createFormikFields<UpdateWebsiteFormValues>();

interface Props {
    show: boolean;
    onClose(): void;
}

export const UpdateWebsiteModal: React.FC<Props> = ({ show, onClose }) => {
    const entity = useEntity();
    const editEntity = useEditEntityMutation(entity);
    const { error } = useToaster();

    const formik = useFormik<UpdateWebsiteFormValues>({
        initialValues: {
            website: entity.profile?.website ?? "",
        },
        validate: validateFormikWithZodSchema(updateWebsiteFormSchema),
        onSubmit: submitHelper({
            handler: async ({ website }) => {
                try {
                    await editEntity.mutateAsync({
                        profile: { ...entity.profile, website },
                    });
                    onClose();
                } catch {
                    error("Failed to update profile");
                }
            },
        }),
    });

    return (
        <PlainModal show={show} onHide={onClose}>
            <StandardModalHeader>Update Website</StandardModalHeader>
            <FormikProvider value={formik}>
                <Form>
                    <VStack gap={16}>
                        <fields.Text
                            name="website"
                            label="Website"
                            showErrorMessage={false}
                            size="xl"
                        />
                        <EditModalFooter
                            onClose={onClose}
                            isValid={formik.isValid}
                            isSubmitting={formik.isSubmitting}
                        />
                    </VStack>
                </Form>
            </FormikProvider>
        </PlainModal>
    );
};
